import React, { useMemo, useSyncExternalStore } from 'react'
import { CartesianGrid, Line, LineChart, ReferenceArea, ResponsiveContainer } from 'recharts'
import { Stack, useTheme } from '@mui/material'
import { XAxisAI360 } from './XAxisAI360'
import ToolTipAI360 from './ToolTipAI360'
import { AI360_LINE_CHART_CONFIG } from 'src/Libs/ai-360/consts'
import { AI360ChartData, AI360ChartDataKeys } from 'src/Libs/ai-360/types'
import YAxisAI360Left from './YAxisAI360Left'
import YAxisAI360Right from './YAxisAI360Right'
import { getZeroFeedInPriceRanges, onMouseLeaveAI360Chart, onMouseMoveAI360Chart } from 'src/Libs/ai-360/utils'
import { useAI360InitializeCurrentDateForTooltip, useAI360RegisterChartInstance } from 'src/Libs/ai-360/hooks'
import { LineChartStore } from 'src/Libs/ai-360/store'

interface Props {
  chartData: AI360ChartData[]
  from: Date
  to: Date
  syncId?: string
}

/**
 * shows biaxial line chart for power price and pv production
 */
const AI360LineChart: React.FC<Props> = ({ chartData, from, to, syncId }) => {
  const theme = useTheme()
  /* set the tooltip at current time on mount or date change */
  const containerRef = useAI360InitializeCurrentDateForTooltip({ chartData, from })
  const { isLegendEnabled } = useSyncExternalStore(LineChartStore.subscribe, LineChartStore.get)

  const registerChartInstance = useAI360RegisterChartInstance()

  const xAxisAI360 = XAxisAI360({ dateFrom: from, dateTo: to })
  const tooltipAI360 = ToolTipAI360({})
  const yAxisAI360Left = YAxisAI360Left({})
  const yAxisAI360Right = YAxisAI360Right({})

  const zeroFeedInPriceRanges = useMemo(() => getZeroFeedInPriceRanges(chartData), [chartData])

  return (
    <Stack ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer width={'100%'} height={'99%'} style={{ paddingRight: 1 }}>
        <LineChart
          ref={registerChartInstance}
          syncId={syncId}
          data={chartData}
          margin={{
            top: 30,
            right: 0,
            left: 0,
            bottom: 0,
          }}
          onMouseMove={state => onMouseMoveAI360Chart(state, chartData)}
          onMouseLeave={() => onMouseLeaveAI360Chart(chartData)}
        >
          {isLegendEnabled.feedInPrice &&
            zeroFeedInPriceRanges.map((range, i) => (
              <ReferenceArea
                key={range.x1}
                yAxisId="left"
                x1={range.x1}
                x2={range.x2}
                fill={theme.palette.error.light}
                fillOpacity={0.5}
                strokeOpacity={0}
                ifOverflow="visible"
              />
            ))}

          <CartesianGrid vertical={false} stroke={theme.palette.shades.shade03} strokeWidth={'1px'} />

          {xAxisAI360}
          {yAxisAI360Left}
          {yAxisAI360Right}
          {tooltipAI360}

          {isLegendEnabled.powerPrice && (
            <Line
              dataKey={AI360ChartDataKeys.powerPrice}
              yAxisId={'left'}
              type="stepAfter"
              strokeLinecap="round"
              strokeLinejoin="round"
              stroke={AI360_LINE_CHART_CONFIG.powerPrice.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: theme.palette.grey.grey00 }}
              isAnimationActive={false}
            />
          )}
          {isLegendEnabled.solarData && (
            <Line
              dataKey={AI360ChartDataKeys.solarProduction}
              yAxisId="right"
              type="monotone"
              stroke={AI360_LINE_CHART_CONFIG.solarProduction.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: theme.palette.grey.grey00 }}
              isAnimationActive={false}
            />
          )}
          {isLegendEnabled.solarData && (
            <Line
              dataKey={AI360ChartDataKeys.solarForecast}
              yAxisId="right"
              type="monotone"
              stroke={AI360_LINE_CHART_CONFIG.solarForecast.color}
              strokeWidth={2}
              strokeDasharray={'3 3'}
              dot={false}
              activeDot={{ r: 4, fill: theme.palette.grey.grey00 }}
              isAnimationActive={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Stack>
  )
}

export default AI360LineChart
